import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { api } from '../consts/url';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, Observable, throwError } from 'rxjs';
import {
  ACCESS_TOKEN_KEY,
  AUTH_PROVIDER_ID_KEY,
  EXPIRES_IN_KEY,
  ID_TOKEN_KEY,
  LocalStorageService,
  Auth0Model,
  AuthModel,
  parseJwt,
} from '@ui';
import { SharedAuthSessionService } from '@share-auth-session';
import { calculateExpiryDate, JsonParseIfValid } from '../utils/utils';
import { UserModels } from '@share-models';
import { BYPASS_TOKEN_REFRESH_CHECK } from './authentication-interceptor.service';
import { routerSegments } from '../consts/routes';
import { UniversalLoginService } from './universal-login.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private universalLoginService: UniversalLoginService,
    private sharedAuthSessionService: SharedAuthSessionService,
  ) {
  }

  public storeSession(data: AuthModel.Events.LoginSuccessEventData): void {
    const idTokenPayload = parseJwt<Auth0Model.Token.JwtUserData>(data.idToken)!;
    const expiresAt = calculateExpiryDate(idTokenPayload.exp - idTokenPayload.iat);
    const authProviderId = idTokenPayload.sub;
    this.localStorageService.clearAuthCredentials();
    this.localStorageService.setItemWitchCheck(ID_TOKEN_KEY, data.idToken);
    this.localStorageService.setItemWitchCheck(ACCESS_TOKEN_KEY, data.accessToken);
    this.localStorageService.setItemWitchCheck(EXPIRES_IN_KEY, expiresAt);
    this.localStorageService.setItemWitchCheck(AUTH_PROVIDER_ID_KEY, authProviderId);
  }

  public cleanSession(): void {
    this.localStorageService.clear();
  }

  public getExpiration(): number {
    const expiration =
      this.localStorageService.getItemWithCheck(EXPIRES_IN_KEY);
    if (!expiration) return 0;

    return JsonParseIfValid<number>(expiration) ?? 0;
  }

  public isLoggedIn(): boolean {
    const expiration = this.getExpiration();
    if (expiration) {
      return (
        expiration > Date.now() &&
        !!this.localStorageService.getItemWithCheck(ID_TOKEN_KEY)
      );
    }
    return false;
  }

  public socialLoginByCode(code: string): Observable<Auth0Model.Auth0Response> {
    return this.http.post<Auth0Model.Auth0Response>(
      api.authV2.getTokenByCode,
      {
        code,
        redirectUrl: window.location.origin,
      },
    );
  }

  public logout(params: UserModels.LogoutParams): void {
    const context = new HttpContext().set(BYPASS_TOKEN_REFRESH_CHECK, true);
    const logoutRedirectUrl = this.buildLogoutCallbackUrl(params?.redirectUrl);

    void firstValueFrom(
      this.http.post<{ logoutUrl: string }>(
        api.authV2.logout,
        { redirectUrl: logoutRedirectUrl.toString() },
        { context },
      ),
    )
      .then(({ logoutUrl }) => {
        window.location.href = logoutUrl;
      })
      .catch(() => {
        this.redirectToLogoutCallback(params?.redirectUrl);
      });
  }

  public isLogoutCallbackRoute(url = new URL(window.location.href)): boolean {
    return url.pathname === `/${routerSegments.logout}` || url.pathname === `/${routerSegments.logout}/`;
  }

  public handleLogoutCallback(url = new URL(window.location.href)): void {
    const redirectUrl = url.searchParams.get('redirectUrl') ?? url.origin;

    this.cleanSession();
    this.universalLoginService.redirectToLogin(redirectUrl);
  }

  public async ensureValidSession(options: {
    allowBackgroundRefresh?: boolean;
    isSilentRefresh?: boolean;
  } = {}): Promise<boolean> {
    return this.sharedAuthSessionService.ensureValidSession(
      {
        hasStoredSession: () => this.hasStoredSession(),
        getMsUntilExpiry: () => this.getMsUntilExpiry(),
        refreshSession: () => this.refreshSession(),
      },
      options,
    );
  }

  public renewToken(): Observable<Auth0Model.Auth0Response> {
    const context = new HttpContext().set(BYPASS_TOKEN_REFRESH_CHECK, true);

    return this.http.post<Auth0Model.Auth0Response>(
      api.authV2.refresh,
      {},
      { context },
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        const message = err.error?.error_description ?? err.error?.message ?? String(err.message ?? '');
        this.cleanSession();
        this.logout({ msg: message });
        return throwError(() => err);
      }),
    );
  }

  private getMsUntilExpiry(): number | null {
    const expiresAtRaw = this.localStorageService.getItemWithCheck(EXPIRES_IN_KEY);
    if (!expiresAtRaw) {
      return null;
    }

    try {
      const expiresAt = JSON.parse(expiresAtRaw);

      if (typeof expiresAt !== 'number') {
        return null;
      }

      return expiresAt - Date.now();
    } catch {
      return null;
    }
  }

  private async refreshSession(): Promise<boolean> {
    try {
      const authResult = await firstValueFrom(this.renewToken());

      if (!authResult?.access_token || !authResult?.id_token) {
        this.logout({ msg: 'Session is expired' });
        return false;
      }

      const authData: AuthModel.Events.LoginSuccessEventData = {
        idToken: authResult.id_token,
        accessToken: authResult.access_token,
        expiredAt: authResult.expires_in,
      };
      this.storeSession(authData);
      return true;
    } catch {
      this.logout({ msg: 'Session is expired' });
      return false;
    }
  }

  private hasStoredSession(): boolean {
    return !!this.localStorageService.getItemWithCheck(ID_TOKEN_KEY)
      && !!this.localStorageService.getItemWithCheck(AUTH_PROVIDER_ID_KEY);
  }

  private buildLogoutCallbackUrl(redirectUrl?: string): URL {
    const logoutRedirectUrl = new URL(`/${routerSegments.logout}`, window.location.origin);

    if (redirectUrl) {
      logoutRedirectUrl.searchParams.set('redirectUrl', redirectUrl);
    }

    return logoutRedirectUrl;
  }

  private redirectToLogoutCallback(redirectUrl?: string): void {
    window.location.href = this.buildLogoutCallbackUrl(redirectUrl).toString();
  }

  public async switchTenant(orgId: string): Promise<void> {
    try {
      const authResult = await firstValueFrom(
        this.http.post<Auth0Model.Auth0Response>(
          api.authV2.switchOrganization,
          { orgId },
        ),
      );
      if (authResult?.access_token && authResult?.id_token) {
        this.storeSession({
          idToken: authResult.id_token,
          accessToken: authResult.access_token,
          expiredAt: authResult.expires_in,
        });
        window.location.href = '/';
      }
    } catch {
      this.cleanSession();
    }
  }
}
